import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
let cartQuantityPending = false;
const cartQuantityQueue = new Map();

class CartItemsComponent extends Component {
  #quantityTimers = new Map();
  #debouncedOnChange = (event) => {
    if (!this.isConnected || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    const key = this.refs.cartItemRows[line - 1]?.dataset.cartLineKey;
    if (!key) return;
    clearTimeout(this.#quantityTimers.get(key));
    this.#quantityTimers.set(key, setTimeout(() => {
      this.#quantityTimers.delete(key);
      this.updateQuantity({ line, key, quantity, action: quantity === 0 ? 'clear' : 'change' });
    }, 300));
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#quantityTimers.forEach(clearTimeout);
    this.#quantityTimers.clear();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const key = this.refs.cartItemRows[line - 1]?.dataset.cartLineKey;
    clearTimeout(this.#quantityTimers.get(key));
    this.#quantityTimers.delete(key);
    this.updateQuantity({
      key,
      line,
      quantity: 0,
      action: 'clear',
    });


  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (!this.isConnected) return;
    const key = config.key || this.refs.cartItemRows[config.line - 1]?.dataset.cartLineKey;
    if (!key) return;
    if (cartQuantityPending) {
      cartQuantityQueue.set(key, { sectionId: this.sectionId, config: { ...config, key } });
      return;
    }
    const currentLine = [...this.querySelectorAll('[data-cart-line-key]')].findIndex(row => row.dataset.cartLineKey === key) + 1;
    if (!currentLine) return;
    config = { ...config, line: currentLine };
    const focused = document.activeElement;
    const focusRow = this.contains(focused) ? focused?.closest('[data-cart-line-key]') : null;
    const focus = focusRow ? { key: focusRow.dataset.cartLineKey, name: focused.getAttribute('name'), remove: focused.matches('.cart-items__remove') } : null;
    cartQuantityPending = true;
    this.#showUpdateError('');
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: key,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then(async (response) => {
        const data = typeof response?.json === 'function' ? await response.json() : response;
        if (!data || typeof data !== 'object' || (!data.errors && !Number.isFinite(data.item_count))) return { sections: { [this.sectionId]: await this.#refreshCartSection() } };
        if (response.ok === false && !data.errors) throw new Error('Cart update failed');
        return data;
      })
      .then(async (parsedResponseText) => {

        resetShimmer(this);

        if (parsedResponseText.errors) {
          const lineKey = this.refs.cartItemRows[line - 1]?.dataset.cartLineKey;
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
          const cart = this.isConnected ? this : document.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`);
          const rows = [...(cart?.querySelectorAll('[data-cart-line-key]') || [])];
          const row = rows.find((item) => item.dataset.cartLineKey === lineKey);
          if (row) {
            const message = row.querySelector('.cart-item__error-text');
            const container = row.querySelector('.cart-items__error');
            if (message && container) {
              message.textContent = parsedResponseText.errors;
              container.classList.remove('hidden');
            }
          } else {
            cart?.#showUpdateError(parsedResponseText.errors);
          }
          const itemCount = parseInt(cart?.querySelector('[ref="cartItemCount"]')?.textContent || '0', 10);
          cart?.dispatchEvent(new CartUpdateEvent({}, this.sectionId, {
            itemCount,
            source: 'cart-items-component',
          }));
          return;
        }

        const sectionHTML = parsedResponseText.sections?.[this.sectionId];
        if (typeof sectionHTML !== 'string') {
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
          this.dispatchEvent(new CartUpdateEvent({}, this.sectionId, {
            itemCount: parsedResponseText.item_count,
            source: 'cart-items-component',
          }));
          return;
        }
        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch(async (error) => {
        console.error(error);
        try {
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
          const cart = this.isConnected ? this : document.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`);
          const itemCount = parseInt(cart?.querySelector('[ref="cartItemCount"]')?.textContent || '0', 10);
          cart?.dispatchEvent(new CartUpdateEvent({}, this.sectionId, {
            itemCount,
            source: 'cart-items-component',
          }));
        } catch (refreshError) {
          console.error(refreshError);
        }
        resetShimmer(this);
        this.querySelectorAll('input[name="updates[]"]').forEach((input) => {
          input.value = input.defaultValue;
        });
        this.#showUpdateError(this.querySelector('[data-cart-update-error]')?.dataset.message || '');
      })
      .finally(() => {
        cartQuantityPending = false;
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        const cart = this.isConnected ? this : document.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`);
        if (focus && document.activeElement === document.body) {
          const row = [...(cart?.querySelectorAll('[data-cart-line-key]') || [])].find(item => item.dataset.cartLineKey === focus.key);
          const replacement = row && (focus.remove ? row.querySelector('.cart-items__remove') : [...row.querySelectorAll('button, input')].find(control => control.getAttribute('name') === focus.name));
          const fallback = cart?.querySelector('.quantity-selector button') || cart?.closest('cart-drawer-component')?.querySelector('.cart-drawer__close-button');
          (replacement || fallback)?.focus({ preventScroll: true });
        }
        for (const [queuedKey, queued] of cartQuantityQueue) {
          cartQuantityQueue.delete(queuedKey);
          const owner = document.querySelector(`cart-items-component[data-section-id="${queued.sectionId}"]`);
          if (owner?.isConnected && [...owner.querySelectorAll('[data-cart-line-key]')].some(row => row.dataset.cartLineKey === queuedKey)) {
            owner.updateQuantity(queued.config);
            break;
          }
        }
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    document.querySelectorAll('cart-items-component').forEach((cart) => {
      cart.classList.add('cart-items-disabled');
      cart.setAttribute('aria-busy', 'true');
      cart.querySelectorAll('button:not(:disabled), input:not(:disabled)').forEach((control) => {
        control.dataset.cartUpdateDisabled = '';
        control.disabled = true;
      });
    });
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    document.querySelectorAll('cart-items-component').forEach((cart) => {
      cart.classList.remove('cart-items-disabled');
      cart.removeAttribute('aria-busy');
      cart.querySelectorAll('[data-cart-update-disabled]').forEach((control) => {
        control.disabled = false;
        delete control.dataset.cartUpdateDisabled;
      });
    });
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  #showUpdateError(message) {
    const cart = this.isConnected ? this : document.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`) || this;
    const error = cart.querySelector('[data-cart-update-error]');
    if (error) {
      error.textContent = message;
      error.hidden = !message;
    }
  }

  #refreshCartSection() {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);
      request.open('GET', url.href);
      request.timeout = 15000;
      request.onload = () => request.status >= 200 && request.status < 300 && request.responseText.includes('cart-items-component') ? resolve(request.responseText) : reject(new Error('Cart refresh failed'));
      request.onerror = request.ontimeout = () => reject(new Error('Cart refresh failed'));
      request.send();
    });
  }

  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
